import {
  ConsoleOutputLevel,
  DepID_MeasurementSchedulerRun,
  DepID_SchedulerInitializePopulation,
  DepID_SchedulerOutputDiagnosticsMessage,
  DepID_SchedulerReschedule,
  DepID_SchedulerScheduleNextMeasurement,
  DepID_SchedulerScheduleNextMeasurementSoon,
  DepID_SchedulerUpdatePopulation,
  HasValue,
  OutputToConsoleFunc,
  SessionMetadata,
} from "../@types"
import { ApplicationEvents } from "./applicationEvents"
import { Calculator } from "./intervalCalculator"
import makeExecutor from "./makeExecutor"
import { defaultSessionProcessFunc } from "./openinsights/defaultSessionProcessFunc"
import { PerformanceEntryManager } from "./openinsights/resourceTiming"
import { Executable } from "./openinsights/start"
import { PlatformTestObject } from "./platformTestObject"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"

export type Dependencies = {
  applicationEvents: ApplicationEvents
  callClearTimeout: (depID: number, timeoutID: number) => void
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearResourceTimings: () => void
  /**
   * An instance of {@link Calculator} used to calculate a
   * fuzzy delay given a target frequency.
   */
  intervalCalculator: Calculator
  maxTargetFrequency: HasValue<number>
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

type ScheduledMeasurementInfo = {
  lastScheduledAt: Date
  measureAfter: Date
}

type ScheduleRecord = {
  testObjectConfig: PlatformTestObject
  sessionMetadata: SessionMetadata
  scheduledMeasurementInfo?: ScheduledMeasurementInfo
}

export function shouldSkipMeasurementsForDormancy(
  trackerBlockingDetected: boolean,
  wasBlockedLastRun: boolean,
): {
  shouldSkip: boolean
  logStateTransition: boolean
  newBlockedState: boolean
} {
  const logStateTransition =
    (trackerBlockingDetected && !wasBlockedLastRun) ||
    (!trackerBlockingDetected && wasBlockedLastRun)

  return {
    shouldSkip: trackerBlockingDetected,
    logStateTransition,
    newBlockedState: trackerBlockingDetected,
  }
}

export function findMeasurementsDue(
  scheduleRecords: ScheduleRecord[],
  now: Date,
): ScheduleRecord[] {
  return scheduleRecords.filter(
    (schedulerRecord) =>
      schedulerRecord.scheduledMeasurementInfo &&
      now > schedulerRecord.scheduledMeasurementInfo.measureAfter,
  )
}

export function calculateNextMeasurementTime(
  now: Date,
  targetFrequency: number,
  intervalCalculator: Calculator,
  immediate: boolean = false,
): Date {
  if (immediate) {
    return now
  }

  const delayInMilliseconds =
    intervalCalculator.calculateInterval(targetFrequency)
  return new Date(now.getTime() + delayInMilliseconds)
}

export function isSessionConfigStale(
  now: Date,
  lastUpdatedAt: Date | undefined,
  maxAgeInMilliseconds: number = 50 * 60 * 1000,
): boolean {
  if (!lastUpdatedAt) {
    return true
  }

  const diffInMilliseconds = now.valueOf() - lastUpdatedAt.valueOf()
  return diffInMilliseconds > maxAgeInMilliseconds
}

export interface Scheduler {
  /**
   * Initializes the test object population and schedules measurements if necessary.
   * @param population A list of {@link PlatformTestObject} objects.
   * @param skipScheduling A set of measurements to skip scheduling. For context,
   * these are the ones that will be delegated to OpenInsights to run when the page
   * first loads. Each of those schedules a follow-up measurement when it completes.
   */
  initializePopulation(
    population: PlatformTestObject[],
    sessionMetadata: SessionMetadata,
    skipScheduling: PlatformTestObject[],
  ): void
  outputDiagnosticMessage(): void
  run(): void
  reschedule(): void
  scheduleNextMeasurement(testObjectConfig: PlatformTestObject): void
  scheduleNextMeasurementSoon(testObjectConfig: PlatformTestObject): void
  /**
   * Update the test object population after a session configuration refresh.
   */
  updatePopulation(
    population: PlatformTestObject[],
    sessionMetadata: SessionMetadata,
  ): void
}

export const createMeasurementScheduler: (
  dependencies: Dependencies,
) => Scheduler = (d) => new SchedulerImpl(d)

const newFutureMeasurementMessage = (dopplerPlatformID: string, after?: Date) =>
  `Measuring ${dopplerPlatformID} after ${after}`

const newQueuedMeasurementsLogMessage = (
  due: ScheduleRecord[],
  toQueue: ScheduleRecord[],
  now: Date,
) => {
  const getMeasurementLogLine = (scheduleRecord: ScheduleRecord) =>
    // eslint-disable-next-line max-len
    `${scheduleRecord.testObjectConfig.dopplerPlatformID} (${scheduleRecord.testObjectConfig.testObjectSize})`
  const messageBuffer = [`Measurements due at ${now}:`]
  messageBuffer.push(
    ...due.map((scheduleRecord) => getMeasurementLogLine(scheduleRecord)),
  )
  messageBuffer.push("\nMeasurements to queue:")
  messageBuffer.push(
    ...toQueue.map((scheduleRecord) => getMeasurementLogLine(scheduleRecord)),
  )
  return messageBuffer.join("\n")
}

const newDifferenceInTargetFrequencyDetectedMessage = (
  dopplerPlatformID: string,
  from: number,
  to: number,
) =>
  `Difference detected in target frequency for ${dopplerPlatformID} (from: ${from}; to: ${to})`

const newScheduleDiagnosticsMessage = (schedule: ScheduleRecord[], at: Date) =>
  schedule
    .reduce(
      (result, scheduleRecord) => {
        const scheduleInfo = scheduleRecord.scheduledMeasurementInfo
        const id = scheduleRecord.testObjectConfig.dopplerPlatformID
        result.push(
          scheduleInfo
            ? `${id} will be measured after ${scheduleInfo.measureAfter}`
            : `${id} currently unscheduled`,
        )
        return result
      },
      [`Measurement Scheduler Diagnostics as of ${at}`],
    )
    .join("\n")

const newMeasurementSkippedMessage = (dopplerPlatformID: string) =>
  `Skipping measurement for ${dopplerPlatformID} (measurements disabled)`

const newTrackerBlockingDormancyMessage = () =>
  "Measurements paused due to tracker blocking detection - continuing config updates"

const newMeasurementsResumedMessage = () =>
  "Measurements resumed - tracker blocking no longer detected"

class SchedulerImpl implements Scheduler {
  /**
   * An list of records, each referencing a platform test object and information
   * about when it should be measured. Whenever it is updated, it is sorted such
   * that unscheduled items come first, followed by scheduled items in increasing
   * order of datetime due.
   */
  private _scheduleRecords: ScheduleRecord[] = []
  private _schedulerLocked = false
  private _wasBlockedLastRun = false

  constructor(private _dependencies: Dependencies) {}

  private _sortScheduleRecords() {
    this._scheduleRecords.sort((left, right) =>
      // Put unscheduled ones first
      !left.scheduledMeasurementInfo && !right.scheduledMeasurementInfo
        ? 0
        : !left.scheduledMeasurementInfo
          ? -1
          : !right.scheduledMeasurementInfo
            ? 1
            : left.scheduledMeasurementInfo.measureAfter <
                right.scheduledMeasurementInfo.measureAfter
              ? -1
              : left.scheduledMeasurementInfo.measureAfter >
                  right.scheduledMeasurementInfo.measureAfter
                ? 1
                : 0,
    )
  }

  /**
   * Called when a change occurs that may affect one or more existing scheduled
   * measurements.
   */
  reschedule(): void {
    this._dependencies.outputToConsole(
      ConsoleOutputLevel.info,
      () =>
        "Rescheduling (max target frequency " +
        this._dependencies.maxTargetFrequency.value +
        ")",
    )
    const now = this._dependencies.newDate(DepID_SchedulerReschedule)
    this._scheduleRecords.forEach((record) => {
      // This is handled similar to how updatePopulation (re-)schedules a measurement
      // when the test object configuration changes.
      const scheduleInfo = record.scheduledMeasurementInfo
      const startingFrom = scheduleInfo ? scheduleInfo.lastScheduledAt : now
      // If a measurement was already scheduled, it gets rescheduled now.
      record.scheduledMeasurementInfo = {
        lastScheduledAt: now,
        measureAfter: calculateNextMeasurementTime(
          startingFrom,
          record.testObjectConfig.targetFrequency,
          this._dependencies.intervalCalculator,
        ),
      }
    })
    // Keep the schedule records sorted
    this._sortScheduleRecords()
    this.outputDiagnosticMessage()
  }

  run() {
    this._dependencies.outputToConsole(
      ConsoleOutputLevel.debug,
      () =>
        `Scheduler run ${this._dependencies.newDate(DepID_MeasurementSchedulerRun)}`,
    )
    if (!this._schedulerLocked) {
      this._schedulerLocked = true
      const unlock = () => {
        this._schedulerLocked = false
      }

      const dormancyResult = shouldSkipMeasurementsForDormancy(
        this._dependencies.runtimeValues.trackerBlockingDetected,
        this._wasBlockedLastRun,
      )

      // Log state transitions only
      if (dormancyResult.logStateTransition) {
        if (dormancyResult.newBlockedState) {
          this._dependencies.outputToConsole(ConsoleOutputLevel.info, () =>
            newTrackerBlockingDormancyMessage(),
          )
        } else {
          this._dependencies.outputToConsole(ConsoleOutputLevel.info, () =>
            newMeasurementsResumedMessage(),
          )
        }
      }

      // Update state for next run
      this._wasBlockedLastRun = dormancyResult.newBlockedState

      if (dormancyResult.shouldSkip) {
        unlock()
        return
      }

      const makeSkipMeasurementExecutor: (
        scheduler: SchedulerImpl,
        scheduleRecord: ScheduleRecord,
      ) => Executable = (
        scheduler: SchedulerImpl,
        scheduleRecord: ScheduleRecord,
      ) => {
        return {
          execute: () => {
            scheduler._dependencies.outputToConsole(
              ConsoleOutputLevel.debug,
              () =>
                newMeasurementSkippedMessage(
                  scheduleRecord.testObjectConfig.dopplerPlatformID,
                ),
            )
            scheduler.scheduleNextMeasurement(scheduleRecord.testObjectConfig)
            scheduler.outputDiagnosticMessage()
            return Promise.resolve()
          },
        }
      }

      const now = this._dependencies.newDate(DepID_MeasurementSchedulerRun)
      const sessionConfigLastUpdatedAt =
        this._dependencies.runtimeValues.sessionConfigLastUpdatedAt

      // Use extracted staleness check function
      if (isSessionConfigStale(now, sessionConfigLastUpdatedAt)) {
        // Put measurements on pause until after the next sessconf refresh
        this._dependencies.outputToConsole(
          ConsoleOutputLevel.info,
          () => "Measurements paused",
        )
        unlock()
        return
      }

      // Use extracted function to find measurements due
      const measurementsDue = findMeasurementsDue(this._scheduleRecords, now)

      if (measurementsDue.length) {
        // Cap the number of measurements per interval
        const maxMeasurementsPerInterval = 3
        const measurementsToQueue = measurementsDue.slice(
          0,
          maxMeasurementsPerInterval,
        )
        this._dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
          newQueuedMeasurementsLogMessage(
            measurementsDue,
            measurementsToQueue,
            now,
          ),
        )
        defaultSessionProcessFunc(
          measurementsToQueue.map((scheduleRecord) =>
            this._dependencies.runtimeValues.measurementsEnabled
              ? makeExecutor(
                  {
                    applicationEvents: this._dependencies.applicationEvents,
                    callClearTimeout: this._dependencies.callClearTimeout,
                    callSendBeacon: this._dependencies.callSendBeacon,
                    callSetTimeout: this._dependencies.callSetTimeout,
                    callClearResourceTimings:
                      this._dependencies.callClearResourceTimings,
                    callFetch: this._dependencies.callFetch,
                    maxTargetFrequency: this._dependencies.maxTargetFrequency,
                    measurementScheduler: this,
                    newAbortController: this._dependencies.newAbortController,
                    newDate: this._dependencies.newDate,
                    newEpochTimestamp: this._dependencies.newEpochTimestamp,
                    newMonotonicTimestamp:
                      this._dependencies.newMonotonicTimestamp,
                    newRandomNumber: this._dependencies.newRandomNumber,
                    outputToConsole: this._dependencies.outputToConsole,
                    performanceEntryAwaitTimeouts:
                      this._dependencies.performanceEntryAwaitTimeouts,
                    performanceEntryManager:
                      this._dependencies.performanceEntryManager,
                    runtimeValues: this._dependencies.runtimeValues,
                  },
                  scheduleRecord.testObjectConfig,
                  scheduleRecord.sessionMetadata,
                  false,
                )
              : makeSkipMeasurementExecutor(this, scheduleRecord),
          ),
        ).then(unlock, unlock)
      } else {
        unlock()
      }
    } else {
      this._dependencies.outputToConsole(
        ConsoleOutputLevel.debug,
        () => "Skipping run because the scheduler is locked",
      )
    }
  }

  private _scheduleMeasurement(
    testObjectConfig: PlatformTestObject,
    immediate: boolean,
    depID: number,
  ) {
    const record = this._scheduleRecords.find((scheduleRecord) =>
      matchTestObjectConfigIDs(
        scheduleRecord.testObjectConfig,
        testObjectConfig,
      ),
    )
    if (record) {
      const now = this._dependencies.newDate(depID)
      const when = calculateNextMeasurementTime(
        now,
        record.testObjectConfig.targetFrequency,
        this._dependencies.intervalCalculator,
        immediate,
      )
      record.scheduledMeasurementInfo = {
        lastScheduledAt: now,
        measureAfter: when,
      }
      // Keep the schedule records sorted
      this._sortScheduleRecords()
      const scheduleInfo = record.scheduledMeasurementInfo
      if (scheduleInfo) {
        this._dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
          newFutureMeasurementMessage(
            testObjectConfig.dopplerPlatformID,
            scheduleInfo.measureAfter,
          ),
        )
      }
    }
  }

  scheduleNextMeasurement(testObjectConfig: PlatformTestObject) {
    this._scheduleMeasurement(
      testObjectConfig,
      false,
      DepID_SchedulerScheduleNextMeasurement,
    )
  }

  scheduleNextMeasurementSoon(testObjectConfig: PlatformTestObject) {
    this._scheduleMeasurement(
      testObjectConfig,
      true,
      DepID_SchedulerScheduleNextMeasurementSoon,
    )
  }

  initializePopulation(
    population: PlatformTestObject[],
    sessionMetadata: SessionMetadata,
    skipScheduling: PlatformTestObject[],
  ) {
    const skipSet = new Set(skipScheduling)
    this._scheduleRecords.push(
      ...population.map((testObjectConfig) => ({
        testObjectConfig,
        sessionMetadata,
      })),
    )
    const now = this._dependencies.newDate(DepID_SchedulerInitializePopulation)
    this._scheduleRecords.forEach((schedulerRecord) => {
      const testObjectConfig = schedulerRecord.testObjectConfig
      if (!skipSet.has(testObjectConfig)) {
        const measureAfter = calculateNextMeasurementTime(
          now,
          testObjectConfig.targetFrequency,
          this._dependencies.intervalCalculator,
        )
        schedulerRecord.scheduledMeasurementInfo = {
          lastScheduledAt: now,
          measureAfter,
        }
        const scheduleInfo = schedulerRecord.scheduledMeasurementInfo
        if (scheduleInfo) {
          this._dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
            newFutureMeasurementMessage(
              testObjectConfig.dopplerPlatformID,
              scheduleInfo.measureAfter,
            ),
          )
        }
      }
    })
    // Keep the schedule records sorted
    this._sortScheduleRecords()
  }

  /**
   * Update the test object population after a session configuration refresh.
   */
  updatePopulation(
    population: PlatformTestObject[],
    sessionMetadata: SessionMetadata,
  ) {
    // Remove test objects no longer configured
    this._scheduleRecords = this._scheduleRecords.filter((scheduleRecord) =>
      population.some((incomingTestObjectConfig) =>
        matchTestObjectConfigIDs(
          incomingTestObjectConfig,
          scheduleRecord.testObjectConfig,
        ),
      ),
    )
    const now = this._dependencies.newDate(DepID_SchedulerUpdatePopulation)
    population.forEach((incomingTestObjectConfig) => {
      const record = this._scheduleRecords.find((scheduleRecord) =>
        matchTestObjectConfigIDs(
          scheduleRecord.testObjectConfig,
          incomingTestObjectConfig,
        ),
      )
      if (record) {
        // Update the existing record's configuration but only change
        // the scheduled information if the target frequency has changed
        const targetFrequencyChanged =
          record.testObjectConfig.targetFrequency !==
          incomingTestObjectConfig.targetFrequency
        if (targetFrequencyChanged) {
          this._dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
            newDifferenceInTargetFrequencyDetectedMessage(
              incomingTestObjectConfig.dopplerPlatformID,
              record.testObjectConfig.targetFrequency,
              incomingTestObjectConfig.targetFrequency,
            ),
          )
        }
        record.testObjectConfig = incomingTestObjectConfig
        record.sessionMetadata = sessionMetadata
        const scheduleInfo = record.scheduledMeasurementInfo
        if (!scheduleInfo || (scheduleInfo && targetFrequencyChanged)) {
          const startingFrom = scheduleInfo ? scheduleInfo.lastScheduledAt : now
          record.scheduledMeasurementInfo = {
            lastScheduledAt: now,
            measureAfter: calculateNextMeasurementTime(
              startingFrom,
              incomingTestObjectConfig.targetFrequency,
              this._dependencies.intervalCalculator,
            ),
          }
        }
      } else {
        this._scheduleRecords.push({
          testObjectConfig: incomingTestObjectConfig,
          sessionMetadata,
          scheduledMeasurementInfo: {
            lastScheduledAt: now,
            measureAfter: calculateNextMeasurementTime(
              now,
              incomingTestObjectConfig.targetFrequency,
              this._dependencies.intervalCalculator,
            ),
          },
        })
      }
    })
    // Keep the schedule records sorted
    this._sortScheduleRecords()
    this.outputDiagnosticMessage()
  }

  outputDiagnosticMessage() {
    this._dependencies.outputToConsole(ConsoleOutputLevel.debug, () =>
      newScheduleDiagnosticsMessage(
        this._scheduleRecords,
        this._dependencies.newDate(DepID_SchedulerOutputDiagnosticsMessage),
      ),
    )
  }
}

function matchTestObjectConfigIDs(
  first: PlatformTestObject,
  second: PlatformTestObject,
) {
  return (
    first.dopplerPlatformID === second.dopplerPlatformID &&
    first.testObjectSize === second.testObjectSize
  )
}
